import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableSortLabel,
  TablePagination,
  IconButton,
  Tooltip,
  InputAdornment,
  Button,
  Collapse,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useAuth } from '../../context/AuthContext';
import DocumentViewerDialog from '../../components/DocumentViewerDialog';

interface GeneratedDocument {
  id: number;
  userId: number;
  templateId: number | null;
  documentName: string | null;
  createdAt: string;
  template: { id: number; templateName: string | null } | null;
  user: { id: number; name: string } | null;
}

interface UserGroup {
  userId: number;
  userName: string;
  documents: GeneratedDocument[];
  lastGenerated: string;
}

type DocSortColumn = 'documentName' | 'createdAt';
type SortDirection = 'asc' | 'desc';

function UserRow({
  group,
  onView,
  onDownload,
  onDelete,
}: {
  group: UserGroup;
  onView: (d: GeneratedDocument) => void;
  onDownload: (d: GeneratedDocument) => void;
  onDelete: (id: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const [sortColumn, setSortColumn] = useState<DocSortColumn>('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const handleSort = (col: DocSortColumn) => {
    if (sortColumn === col) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(col);
      setSortDirection('asc');
    }
  };

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleString('en-US', {
      year: '2-digit', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: true,
    });

  const sorted = [...group.documents].sort((a, b) => {
    let aVal: string | number;
    let bVal: string | number;
    if (sortColumn === 'createdAt') {
      aVal = new Date(a.createdAt).getTime();
      bVal = new Date(b.createdAt).getTime();
    } else {
      aVal = (a[sortColumn] || '').toLowerCase();
      bVal = (b[sortColumn] || '').toLowerCase();
    }
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  return (
    <>
      <TableRow
        hover
        onClick={() => setOpen(!open)}
        sx={{ cursor: 'pointer', '& > *': { borderBottom: open ? 'none' : undefined } }}
      >
        <TableCell sx={{ width: 40, pr: 0 }}>
          <IconButton size="small">
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon fontSize="small" color="action" />
            <Typography fontWeight={600}>{group.userName}</Typography>
          </Box>
        </TableCell>
        <TableCell align="center">
          <Typography variant="body2">{group.documents.length}</Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2">{formatDate(group.lastGenerated)}</Typography>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={4} sx={{ py: 0, px: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ mx: 2, my: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <TableSortLabel
                        active={sortColumn === 'documentName'}
                        direction={sortColumn === 'documentName' ? sortDirection : 'asc'}
                        onClick={() => handleSort('documentName')}
                      >
                        Document Name
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Source Template</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <TableSortLabel
                        active={sortColumn === 'createdAt'}
                        direction={sortColumn === 'createdAt' ? sortDirection : 'asc'}
                        onClick={() => handleSort('createdAt')}
                      >
                        Generated Date
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sorted.map((d) => (
                    <TableRow key={d.id} hover>
                      <TableCell>{d.documentName || 'N/A'}</TableCell>
                      <TableCell>{d.template?.templateName || 'N/A'}</TableCell>
                      <TableCell>{formatDate(d.createdAt)}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="View">
                            <IconButton size="small" color="info" onClick={(e) => { e.stopPropagation(); onView(d); }}>
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Download">
                            <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); onDownload(d); }}>
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); onDelete(d.id); }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function DocumentList() {
  const { authFetch } = useAuth();

  const [documents, setDocuments] = useState<GeneratedDocument[]>([]);
  const [nameSearch, setNameSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [viewingDoc, setViewingDoc] = useState<GeneratedDocument | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalUsers, setTotalUsers] = useState(0);

  const fetchDocuments = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        page: String(page + 1),
        limit: String(rowsPerPage),
      });
      if (nameSearch) params.set('search', nameSearch);
      const res = await authFetch(`/api/documents?${params}`);
      if (res.ok) {
        const data = await res.json();
        setDocuments(data.documents);
        setTotalUsers(data.pagination.totalUsers);
      }
    } catch (err) {
      console.error('Error fetching documents:', err);
    }
  }, [authFetch, page, rowsPerPage, nameSearch]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setNameSearch(searchInput);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this document?')) return;
    try {
      await authFetch(`/api/documents/${id}`, { method: 'DELETE' });
      fetchDocuments();
    } catch (err) {
      console.error('Error deleting document:', err);
    }
  };

  const handleDownload = async (doc: GeneratedDocument) => {
    try {
      const res = await authFetch(`/api/documents/${doc.id}`);
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${doc.documentName || `document-${doc.id}`}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading document:', err);
    }
  };

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleString('en-US', {
      year: '2-digit', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: true,
    });

  const userGroups: UserGroup[] = useMemo(() => {
    const map = new Map<number, UserGroup>();
    for (const doc of documents) {
      const uid = doc.userId;
      if (!map.has(uid)) {
        map.set(uid, {
          userId: uid,
          userName: doc.user?.name || 'Unknown',
          documents: [],
          lastGenerated: doc.createdAt,
        });
      }
      const group = map.get(uid)!;
      group.documents.push(doc);
      if (new Date(doc.createdAt) > new Date(group.lastGenerated)) {
        group.lastGenerated = doc.createdAt;
      }
    }
    return Array.from(map.values()).sort((a, b) =>
      a.userName.toLowerCase().localeCompare(b.userName.toLowerCase())
    );
  }, [documents]);

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>Generated Documents</Typography>

        <TextField
          fullWidth
          size="small"
          placeholder="Search by name..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            },
          }}
          sx={{ mb: 2, maxWidth: 400 }}
        />

        {documents.length === 0 ? (
          <Typography sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
            {nameSearch ? 'No users match your search' : 'No generated documents yet'}
          </Typography>
        ) : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 40 }} />
                  <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">Documents</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Last Generated</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {userGroups.map((group) => (
                  <UserRow
                    key={group.userId}
                    group={group}
                    onView={setViewingDoc}
                    onDownload={handleDownload}
                    onDelete={handleDelete}
                  />
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={totalUsers}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </Paper>

      <DocumentViewerDialog
        open={!!viewingDoc}
        onClose={() => setViewingDoc(null)}
        fetchUrl={viewingDoc ? `/api/documents/${viewingDoc.id}` : ''}
        title={viewingDoc?.documentName || 'Document'}
        subtitle={
          viewingDoc
            ? `${viewingDoc.user?.name || 'Unknown'} · ${viewingDoc.template?.templateName || 'N/A'} · ${formatDate(viewingDoc.createdAt)}`
            : undefined
        }
      />
    </Box>
  );
}
